import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { HeroUIProvider } from "@heroui/react";
import { QueryProvider, AppLoader } from "@/components";
import { AppUpdatePrompt } from "@/components/app-update-prompt";
import { CurrencyProvider } from "@/hooks/currency-context";
import { MenuConfigProvider } from "@/hooks/menu-config-context";
import { useIsStandalone } from "@/hooks";
import { Layout } from "@/views";
import { lazy, Suspense } from "react";

const HomePage = lazy(() => import("@/pages/HomePage").then(m => ({ default: m.HomePage })));
const AboutPage = lazy(() => import("@/pages/AboutPage").then(m => ({ default: m.AboutPage })));
const ContactPage = lazy(() => import("@/pages/ContactPage").then(m => ({ default: m.ContactPage })));
const MenuPage = lazy(() => import("@/pages/MenuPage").then(m => ({ default: m.MenuPage })));
const FullMenuPage = lazy(() => import("@/pages/FullMenuPage").then(m => ({ default: m.FullMenuPage })));
const DailyMealsPage = lazy(() => import("@/pages/DailyMealsPage").then(m => ({ default: m.DailyMealsPage })));
const GalleryPage = lazy(() => import("@/pages/GalleryPage").then(m => ({ default: m.GalleryPage })));
const ReservationsPage = lazy(() => import("@/pages/ReservationsPage").then(m => ({ default: m.ReservationsPage })));
const TestimonialsPage = lazy(() => import("@/pages/TestimonialsPage").then(m => ({ default: m.TestimonialsPage })));
const KidsPage = lazy(() => import("@/pages/KidsPage").then(m => ({ default: m.KidsPage })));
const ProductPage = lazy(() => import("@/pages/ProductPage").then(m => ({ default: m.ProductPage })));
const CareersPage = lazy(() => import("@/pages/CareersPage").then(m => ({ default: m.CareersPage })));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage").then(m => ({ default: m.NotFoundPage })));
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage").then(m => ({ default: m.PrivacyPolicyPage })));
import "./i18n";

function ScrollToTop() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location.pathname]);

  return null;
}


import { initOfflineReviewsSync } from "@/utils/offlineReviewQueue";

function AppContent() {
  const { i18n } = useTranslation();

  useEffect(() => {
    const cleanupSync = initOfflineReviewsSync();
    return cleanupSync;
  }, []);

  useEffect(() => {
    document.documentElement.lang = i18n.language;
    document.documentElement.dir =
      i18n.language === "ar" || i18n.language === "ku" ? "rtl" : "ltr";
  }, [i18n.language]);

  return (
    <>
      <AppLoader />
      <AppUpdatePrompt />
      <ScrollToTop />
      <Layout>
        <Suspense fallback={<div className="flex-1 flex items-center justify-center min-h-screen"><div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div></div>}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/menu" element={<FullMenuPage />} />
            <Route path="/full-menu" element={<MenuPage />} />
            <Route path="/daily-meals" element={<DailyMealsPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/gallery" element={<GalleryPage />} />
            <Route path="/reservations" element={<ReservationsPage />} />
            <Route path="/testimonials" element={<TestimonialsPage />} />
            <Route path="/kids" element={<KidsPage />} />
            <Route path="/product/:slug" element={<ProductPage />} />
            <Route path="/careers" element={<CareersPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </Layout>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <QueryProvider>
        <HeroUIProvider>
          <CurrencyProvider>
            <MenuConfigProvider>
              <AppContent />
            </MenuConfigProvider>
          </CurrencyProvider>
        </HeroUIProvider>
      </QueryProvider>
    </BrowserRouter>
  );
}

export default App;
